import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {ArrUtil} from 'yougile-util'
import {Subtask} from 'yougile-model'
import {DragData} from '@common/drag-data'
import {noPropagate} from '@containers/hooks'
import {dragPropsFromCtl, useDraggable} from '@containers/hooks-draggable'
import {cn} from '@containers/html-utils'
import LineIcon from './line-l.svg'
import VectorIcon from './vector.svg'

export interface SortableProps {
  components: Record<string, React.ReactNode>
  onChange: (keys: string[]) => void
  onDragChange?: (key: string | undefined) => void
  disable?: boolean
  curvedLines?: boolean
  lastSubtask: Subtask
}

// eslint-disable-next-line max-lines-per-function
export const Sortable = (props: SortableProps) => {
  const [state, setState] = useState(() => Object.keys(props.components))
  useLayoutEffect(() => {
    setState(Object.keys(props.components))
  }, [props.components])
  const dragCtl = useDraggable<string>({
    onDrop: () => props.onChange(state)
  })
  const dragOverIndex = state.indexOf(dragCtl.dragOverId)
  const waitDragOverUpdateRef = useRef<boolean>(false)
  useEffect(() => {
    waitDragOverUpdateRef.current = false
  }, [dragCtl.dragOverId])
  useEffect(() => {
    const draggedIndex = state.indexOf(dragCtl.draggedId)
    if (
      !waitDragOverUpdateRef.current &&
      draggedIndex >= 0 &&
      dragOverIndex >= 0 &&
      draggedIndex !== dragOverIndex &&
      dragCtl.draggedId !== dragCtl.dragOverId
    ) {
      setState(keys => ArrUtil.move(keys, draggedIndex, dragOverIndex))
      waitDragOverUpdateRef.current = true
    }
  }, [dragOverIndex])

  useEffect(() => {
    props.onDragChange?.(dragCtl.draggedId)
  }, [dragCtl.draggedId])

  return (
    <>
      {state.map(key => {
        const dragProps = dragPropsFromCtl(key, dragCtl)
        return (
          <div
            key={key.toString()}
            className={cn(
              key === dragCtl.draggedId &&
                'bg-header-old cursor-pointer opacity-25',

              // for accepting draggable items when parent
              // added pointer-events: none (see native-dragging class)
              'pointer-events-auto'
            )}
          >
            <div className={`${props.curvedLines ? 'flex -ml-8' : 'mb-4'}`}>
              {props.curvedLines && (
                <div className="relative text-action-default">
                  {props.disable && props.lastSubtask.id === key ? (
                    <LineIcon />
                  ) : (
                    <>
                      <VectorIcon className="absolute top-0 right-0" />
                      <div className="h-full w-18 bg-[url(/img/line.svg)]" />
                    </>
                  )}
                </div>
              )}
              <div
                draggable={!props.disable}
                onMouseDown={noPropagate}
                {...dragProps}
                onDragStart={e => {
                  if (DragData.get()?.type === 'empty') {
                    dragProps.onDragStart(e)
                  }
                }}
                className="w-full min-w-0 mb-8"
              >
                {props.components[key]}
              </div>
            </div>
          </div>
        )
      })}
    </>
  )
}
